import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, ArrowRight, ChevronRight, Lock } from 'lucide-react';
import { servicesData } from '@/data/servicesData';
import { useCustomImages } from '@/context/ImageContext';

const Footer = () => {
  const navigate = useNavigate();
  const { getImage, openAdminPortal } = useCustomImages();
  const footerLogo = getImage('footerLogo', '/logo.png');

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/stellarsestates', label: 'Facebook' },
    { icon: Instagram, href: 'https://instagram.com/stellarsestates', label: 'Instagram' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'All Services', path: '/services' },
    { name: "Founder's Cut", path: '/founder' },
    { name: 'Blogs & Insights', path: '/blogs' },
    { name: 'ROI Calculator', path: '/#calculator' },
  ];

  const handleNavigation = (path) => {
    if (path.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const id = path.replace('/#', '');
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  return (
    <footer className="w-full text-white bg-[#181F32] border-t border-white/10 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Top Section: Brand, Quick Links, Contact & Connect */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          
          {/* Brand Section */}
          <div className="space-y-5">
            <div className="flex items-center gap-0.5 cursor-pointer group py-1" onClick={() => navigate('/')}>
              <img 
                src={footerLogo} 
                alt="Stellars Estates Logo" 
                className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
              <span className="font-extrabold text-xl sm:text-2xl font-heading tracking-tight">
                Stellars <span className="text-[#F3453F]">Estates</span>
              </span>
            </div>
            <p className="text-white/90 text-sm leading-relaxed font-sans font-medium">
              Performance-driven digital marketing solutions transforming growth-stage businesses through Meta/Google ads, SEO, custom web platforms, and automated funnels.
            </p>
            <div className="pt-2 border-t border-white/10">
              <p className="text-white/80 text-xs font-semibold mb-1">Founded by</p>
              <button 
                onClick={() => navigate('/founder')}
                className="text-[#F3453F] font-extrabold text-sm hover:underline font-heading min-h-[44px] inline-flex items-center"
              >
                Sahil Sharma
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-5">
            <p className="text-lg font-bold font-heading text-white">Quick Links</p>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.path)}
                    className="text-white/90 hover:text-[#F3453F] transition-colors flex items-center gap-2 text-sm font-medium min-h-[44px] py-1 w-full text-left"
                  >
                    <ArrowRight size={14} className="text-[#F3453F] shrink-0" />
                    <span>{link.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information */}
          <div className="space-y-5">
            <p className="text-lg font-bold font-heading text-white">Contact Us</p>
            <div className="space-y-3.5">
              <div className="flex items-center gap-3 text-white group py-1">
                <div className="glass-icon-pod w-11 h-11 p-2.5 flex items-center justify-center flex-shrink-0 text-[#F3453F] group-hover:border-[#F3453F] transition-all duration-300">
                  <Phone size={18} />
                </div>
                <span className="text-sm font-semibold font-sans group-hover:text-white transition-colors">+91 93109 71677</span>
              </div>
              <div className="flex items-center gap-3 text-white group py-1">
                <div className="glass-icon-pod w-11 h-11 p-2.5 flex items-center justify-center flex-shrink-0 text-[#F3453F] group-hover:border-[#F3453F] transition-all duration-300">
                  <Mail size={18} />
                </div>
                <span className="text-sm font-semibold font-sans group-hover:text-white transition-colors">connect@stellarsestates.in</span>
              </div>
              <div className="flex items-center gap-3 text-white group py-1">
                <div className="glass-icon-pod w-11 h-11 p-2.5 flex items-center justify-center flex-shrink-0 text-[#F3453F] group-hover:border-[#F3453F] transition-all duration-300">
                  <MapPin size={18} />
                </div>
                <span className="text-sm font-semibold font-sans group-hover:text-white transition-colors">Raj Nagar Extension, Ghaziabad, India</span>
              </div>
            </div>
          </div>

          {/* Connect & CTA */}
          <div className="space-y-5">
            <p className="text-lg font-bold font-heading text-white">Connect With Us</p>
            <p className="text-white/90 text-sm">Follow our latest growth insights and client updates.</p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="btn-glass btn-glass-secondary btn-glass-sm min-w-[44px] min-h-[44px] w-11 h-11 p-0 rounded-xl flex items-center justify-center"
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
            <button 
              onClick={() => handleNavigation('/#contact')}
              className="w-full mt-2 btn-glass btn-glass-primary py-3.5 min-h-[44px] text-sm font-bold"
            >
              Book Strategy Call
            </button>
          </div>
        </div>

        {/* Middle Section: Full Services Directory */}
        <div className="py-12 border-b border-white/10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xl font-extrabold font-heading text-white">Our Complete Service Directory</p>
              <p className="text-xs sm:text-sm text-white/80 font-sans mt-1">Explore all digital growth, technology, marketing, and consultation solutions.</p>
            </div>
            <button
              onClick={() => handleNavigation('/services')}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-[#F3453F] hover:underline min-h-[44px] px-2"
            >
              <span>Explore All Services</span>
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 text-left">
            {servicesData.map((category) => (
              <div key={category.id} className="space-y-3">
                <p className="text-sm font-extrabold font-heading text-[#F3453F] uppercase tracking-wider flex items-center gap-1.5">
                  <category.icon size={16} className="shrink-0" />
                  <span>{category.title}</span>
                </p>

                {category.hasSubgroups ? (
                  <div className="space-y-3 pl-2 border-l border-white/10">
                    {category.groups.map((group) => (
                      <div key={group.name} className="space-y-1.5">
                        <span className="text-[11px] font-bold text-white/70 uppercase tracking-widest block">
                          {group.name}
                        </span>
                        <ul className="space-y-1">
                          {group.items.map((subItem) => (
                            <li key={subItem.name}>
                              <button
                                onClick={() => handleNavigation(subItem.path)}
                                className="text-xs text-white/90 hover:text-[#F3453F] transition-colors flex items-center gap-1.5 group font-medium min-h-[36px] py-1"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-white/40 group-hover:bg-[#F3453F] transition-colors" />
                                <span>{subItem.name}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-1.5 pl-2 border-l border-white/10">
                    {category.items.map((subItem) => (
                      <li key={subItem.name}>
                        <button
                          onClick={() => handleNavigation(subItem.path)}
                          className="text-xs text-white/90 hover:text-[#F3453F] transition-colors flex items-center gap-1.5 group font-medium min-h-[36px] py-1"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-white/40 group-hover:bg-[#F3453F] transition-colors" />
                          <span>{subItem.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/90">
          <p>
            © 2021-{new Date().getFullYear()} Stellars Estates. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-white/90">
            <Link to="/privacy-policy" className="hover:text-[#F3453F] transition-colors min-h-[44px] inline-flex items-center">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#F3453F] transition-colors min-h-[44px] inline-flex items-center">Terms of Service</Link>
            <button
              onClick={openAdminPortal}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-white/10 hover:bg-[#F3453F] hover:text-white text-white text-xs transition-all font-medium border border-white/20 min-h-[44px]"
              title="Site Image Management"
            >
              <Lock size={12} />
              <span>Admin Portal</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
